import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";
import { useMemo } from "react";
import ChartLegend from "./ChartLegend";
import { STATUS_COLORS } from "./chartTheme";
import { computeDailyUptime, dayState, formatDuration } from "./uptime";

const STATE_COLORS = {
  up: STATUS_COLORS.good,
  partial: STATUS_COLORS.warning,
  down: STATUS_COLORS.critical,
  unknown: STATUS_COLORS.empty,
};

const STATE_LABELS = {
  up: "Up",
  partial: "Partial outage",
  down: "Down",
  unknown: "No data",
};

const formatUptime = (value) =>
  value === null ? "No data" : `${value >= 99.95 ? "100" : value.toFixed(1)}% uptime`;

const dayTooltip = (bucket) => {
  const state = dayState(bucket);
  const parts = [];
  if (bucket.up) parts.push(`Up ${formatDuration(bucket.up)}`);
  if (bucket.down) parts.push(`Down ${formatDuration(bucket.down)}`);
  if (bucket.unknown && state !== "unknown") parts.push(`No data ${formatDuration(bucket.unknown)}`);
  return (
    <Box>
      <Box sx={{ fontWeight: 600 }}>
        {format(bucket.date, "d MMM yyyy")} · {STATE_LABELS[state]}
      </Box>
      {parts.length > 0 && <Box>{parts.join(" · ")}</Box>}
      {bucket.changes > 0 && (
        <Box sx={{ opacity: 0.8 }}>
          {bucket.changes} status change{bucket.changes > 1 ? "s" : ""}
        </Box>
      )}
    </Box>
  );
};

// Status-page style history: one bar per UTC day, colored by that day's state.
const UptimeStrip = ({ title, statusChanges, type, currentUp, checkFailing = false, paused = false, days = 30 }) => {
  const { days: buckets, uptimePercent } = useMemo(
    () => computeDailyUptime(statusChanges, type, currentUp, { days }),
    [statusChanges, type, currentUp, days]
  );

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
        <Typography variant="h6" component="h3" sx={{ fontSize: 15 }}>
          {title}
        </Typography>
        {paused ? (
          <Chip
            size="small"
            label="Paused"
            sx={{
              height: 22,
              fontSize: 12,
              backgroundColor: "rgba(145, 158, 171, 0.16)",
              color: "rgb(99, 115, 129)",
            }}
          />
        ) : checkFailing ? (
          <Chip
            size="small"
            label="Check failing"
            sx={{
              height: 22,
              fontSize: 12,
              backgroundColor: "rgba(255, 171, 0, 0.16)",
              color: "rgb(183, 110, 0)",
            }}
          />
        ) : typeof currentUp === "boolean" && (
          <Chip
            size="small"
            label={currentUp ? "Online" : "Offline"}
            sx={{
              height: 22,
              fontSize: 12,
              backgroundColor: currentUp ? "rgba(34, 197, 94, 0.16)" : "rgba(255, 86, 48, 0.16)",
              color: currentUp ? "rgb(17, 141, 87)" : "rgb(183, 29, 24)",
            }}
          />
        )}
        <Typography sx={{ ml: "auto", fontSize: 13, fontWeight: 600, color: "text.secondary" }}>
          {formatUptime(uptimePercent)}
        </Typography>
      </Box>

      <Box
        role="img"
        aria-label={`${title}: ${formatUptime(uptimePercent)} over the last ${days} days`}
        sx={{ display: "flex", gap: { xs: "2px", sm: "3px" }, height: 36 }}
      >
        {buckets.map((bucket) => {
          const state = dayState(bucket);
          return (
            <Tooltip key={bucket.date.toISOString()} title={dayTooltip(bucket)} arrow>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  borderRadius: "3px",
                  backgroundColor: STATE_COLORS[state],
                  cursor: "default",
                  transition: "opacity 120ms",
                  "&:hover": { opacity: 0.75 },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.75 }}>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{days} days ago</Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Today</Typography>
      </Box>
    </Box>
  );
};

export const UptimeLegend = () => (
  <ChartLegend
    items={["up", "partial", "down", "unknown"].map((state) => ({
      id: state,
      label: STATE_LABELS[state],
      color: STATE_COLORS[state],
    }))}
  />
);

export default UptimeStrip;
